/**
 * 極簡的 IndexedDB 鍵值存取。
 *
 * 為什麼不用 localStorage：配額只有 5MB，而且是**同步**的——
 * 每次存檔都會卡住主執行緒。IndexedDB 配額到幾百 MB 且非同步，
 * 要真的「借用使用者的設備」就得用它。
 *
 * 這裡刻意只做鍵值，不做索引與查詢：桌面需要的就只是「存一包、讀一包」。
 */

export interface KeyValueStore {
  get<T>(key: string): Promise<T | null>
  set(key: string, value: unknown): Promise<void>
  delete(key: string): Promise<void>
}

const STORE = 'kv'

export function openKeyValueStore(dbName: string): KeyValueStore {
  let ready: Promise<IDBDatabase> | null = null

  const db = (): Promise<IDBDatabase> => {
    ready ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 1)
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE)
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB 開啟失敗'))
    })
    return ready
  }

  const run = async <T>(
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => IDBRequest,
  ): Promise<T> => {
    const connection = await db()
    return new Promise<T>((resolve, reject) => {
      const tx = connection.transaction(STORE, mode)
      const request = work(tx.objectStore(STORE))
      request.onsuccess = () => resolve(request.result as T)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB 操作失敗'))
    })
  }

  return {
    async get<T>(key: string): Promise<T | null> {
      const value = await run<T | undefined>('readonly', (store) => store.get(key))
      return value ?? null
    },
    async set(key, value) {
      await run('readwrite', (store) => store.put(value, key))
    },
    async delete(key) {
      await run('readwrite', (store) => store.delete(key))
    },
  }
}

/** 無痕模式或使用者停用時 IndexedDB 會開不起來——退回不存，而不是讓桌面開不了 */
export function isAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined'
  } catch {
    return false
  }
}
